import React from 'react';
import { formatDate, formatNumber } from '@progress/kendo-intl';
import { DataType } from '../../../config/Constants';
import { EnumType } from '../../../config/Enums';
import { IconTemplate, EnumTemplate, FieldsWithTemplates } from './IconTemplate';
import { Col, Row } from 'react-bootstrap';
import { OnDemandIcon } from '../../helpers/IconUtils';
import { Link } from 'react-router-dom';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

export interface IBoxProps {
  title?: string;
  field: string;
  value: any;
  type: DataType;
  format?: string;
  route?: string;
  icon?: string;
  enum?: EnumType;
}

function DisplayBoxBase(props: IBoxProps): React.ReactElement | null {
  const formatValue = () => {
    if (!props.value) return '-';
    switch (props.type) {
      case DataType.Date:
      case DataType.DateTime:
        return props.format ? formatDate(new Date(props.value), props.format) : formatDate(new Date(props.value), 'd');
      case DataType.Integer:
        return props.format
          ? formatNumber(parseInt(props.value), props.format)
          : formatNumber(parseInt(props.value), 'n0');
      case DataType.Decimal:
        return props.format
          ? formatNumber(parseFloat(props.value), props.format)
          : formatNumber(parseFloat(props.value), 'n2');
      case DataType.Currency:
        return props.format
          ? formatNumber(parseFloat(props.value), props.format)
          : formatNumber(parseFloat(props.value), 'c2');
      case DataType.GuidList:
        return props.value.split(';').join('<br/>');
      default:
        return props.value ? props.value : '-';
    }
  };
  const hasValue = () => {
    if (props.type === DataType.PageInfo || props.type === DataType.RoleInfo || props.type === DataType.GuidList)
      return props.value && props.value.length > 0;
    return props.value || props.type === DataType.Switch || props.type === DataType.CheckBox;
  };

  const renderInput = () => {
    return (
      <Col md={10} sm={9} style={{ fontWeight: 300 }}>
        {props.type === DataType.Switch || props.type === DataType.CheckBox ? (
          <IconTemplate field={props.field} value={props.value} />
        ) : (
          <>
            {props.route ? (
              <Link to={props.route} target="_blank noreferrer" style={{ textDecoration: 'none' }}>
                {formatValue()}
                <OnDemandIcon icon={'arrow-right'} className="ms-1" />
              </Link>
            ) : (
              formatValue()
            )}
          </>
        )}
      </Col>
    );
  };

  const renderIconInput = () => {
    if (props.type === DataType.Switch || props.type === DataType.CheckBox)
      return <IconTemplate field={props.field} value={props.value} />;
    return (
      <div className="d-flex align-items-center">
        <IconTemplate field={FieldsWithTemplates.IsoCode} value={props.icon} className="me-1" />
        <span style={{ fontWeight: 300 }} className="ms-1">
          {formatValue()}
        </span>
      </div>
    );
  };
  const renderList = () => {
    const listValues = props.value.split(';');
    const lastIndex = listValues.length - 1;
    return (
      <Col md={10} sm={9} className="align-items-center pt-1 pb-1">
        {listValues.map((e, i) => (
          <p key={i} className={i === lastIndex ? 'mb-0' : 'mb-1'}>
            {e && (
              <>
                <OnDemandIcon icon="globe" size="sm" className="me-1" />
                <span style={{ fontWeight: 300 }}>{e.trim()}</span>
              </>
            )}
          </p>
        ))}
      </Col>
    );
  };
  const renderPageInfo = (icon: IconProp) => {
    const lastIndex = props.value ? props.value.length - 1 : 0;
    return (
      <Col md={10} sm={9} className="align-items-center pt-1 pb-1">
        {props.value?.map((e: any, i: number) => (
          <p key={i} className={i === lastIndex ? 'mb-0' : 'mb-1'}>
            {
              <>
                <OnDemandIcon icon={icon} size="sm" className="me-1" title={e.id} />
                <span style={{ fontWeight: 300 }}>{e.display.trim()}</span>
              </>
            }
          </p>
        ))}
      </Col>
    );
  };
  const renderEnumInput = () => {
    if (props.enum === undefined || props.enum === null) return renderInput();
    return <EnumTemplate type={props.enum} field={props.field} value={props.value} withText={true} />;
  };
  const renderHtmlString = () => {
    return <div className="my-2" dangerouslySetInnerHTML={{ __html: props.value }}></div>;
  };
  return (
    <>
      {hasValue() ? (
        <Row className="border-bottom py-2">
          <Col md={4} className="d-flex align-items-center" style={{ fontWeight: 300 }}>
            {props.title}
          </Col>
          <Col>
            {' '}
            {props.icon
              ? renderIconInput()
              : props.enum !== undefined && props.enum !== null
              ? renderEnumInput()
              : props.type == DataType.PageInfo
              ? renderPageInfo(['far', 'file'])
              : props.type == DataType.RoleInfo
              ? renderPageInfo('shield-halved')
              : props.type == DataType.GuidList
              ? renderList()
              : props.type == DataType.HtmlString
              ? renderHtmlString()
              : renderInput()}
          </Col>
        </Row>
      ) : null}
    </>
  );
}

function DisplayBoxPropsAreEqual(prev: IBoxProps, next: IBoxProps) {
  return (
    prev.field === next.field &&
    prev.value === next.value &&
    prev.title === next.title &&
    prev.type === next.type &&
    prev.enum === next.enum &&
    prev.icon === next.icon
  );
}
export const DisplayBox = React.memo(DisplayBoxBase, DisplayBoxPropsAreEqual);
